import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInAndUp } from 'supertokens-web-js/recipe/thirdparty';

import { useUpdateUserUtm } from 'src/Hooks/Mutations/useUpdateUserUtm';
import { trackEvent } from 'src/Services/Matomo';
import { trackUserLoggedIn, trackUserSignedUp } from 'src/Services/Mixpanel/events';

import styles from './ThirdPartySignInHandler.module.scss';

export interface SuperTokensGeneralError extends Error {
  isSuperTokensGeneralError: true;
}

export const isSuperTokensError = (error: unknown): error is SuperTokensGeneralError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isSuperTokensGeneralError' in error &&
    (error as { isSuperTokensGeneralError?: boolean }).isSuperTokensGeneralError === true &&
    'message' in error
  );
};

const ThirdPartySignInHandler = () => {
  const navigate = useNavigate();
  const effectRan = useRef(false);
  const { mutate } = useUpdateUserUtm();

  useEffect(() => {
    // prevent double execution in dev mode
    if (effectRan.current === true) {
      return;
    }
    effectRan.current = true;

    async function handleThirdPartySignIn() {
      try {
        const response = await signInAndUp();

        if (response.status === 'OK') {
          if (response.createdNewRecipeUser) {
            const utmDataString = sessionStorage.getItem('utm_data');
            if (utmDataString) {
              try {
                const utmData = JSON.parse(utmDataString);

                mutate(utmData);

                sessionStorage.setItem('utm_synced', 'true');
              } catch (e) {
                console.error('Cannot get UTM', e);
              }
            }
          }

          const thirdPartyId = response.user.thirdParty?.[0]?.id ?? 'third_party';
          trackEvent('Auth', `login/register with ${thirdPartyId}`, response.user.emails[0]);
          if (response.createdNewRecipeUser) {
            trackUserSignedUp({ method: thirdPartyId });
          } else {
            trackUserLoggedIn({ method: thirdPartyId });
          }
          navigate('/');
        } else if (response.status === 'SIGN_IN_UP_NOT_ALLOWED') {
          window.alert(response.reason);
          navigate('/auth?error=not_allowed');
        } else {
          window.alert('No email provided by social login. Please use another form of login');

          navigate('/auth?error=no_email');
        }
      } catch (err: unknown) {
        if (isSuperTokensError(err)) {
          window.alert(err.message);
        } else {
          window.alert('Oops! Something went wrong.');
        }
        navigate('/auth?error=unexpected');
      }
    }

    handleThirdPartySignIn();
  }, []);

  return <div className={styles.container}></div>;
};

export default ThirdPartySignInHandler;
